import { FilterableMultiSelect } from '@carbon/react';
import { MultiSelectFilterProps } from '../types';
import styles from './multi-select-filter.module.scss';

type MultiSelectItem = { value: string; label: string };

/**
 * Multi-select filter component using Carbon FilterableMultiSelect.
 * Allows selecting multiple values from a searchable list of options.
 * @param {MultiSelectFilterProps} props - Component props
 * @param {string} props.id - Unique identifier for the filter
 * @param {string} props.label - Display label for the filter
 * @param {string[]} props.value - Array of currently selected values
 * @param {Function} props.onChange - Callback when selection changes
 * @param {Array} props.options - Available options with value/label pairs
 * @param {string} [props.placeholder] - Placeholder text (default: 'Select...')
 * @param {boolean} [props.autoAlign] - Flip the menu to fit when a scrolling panel would clip it
 * @return {JSX.Element} The multi-select filter element
 */
export function MultiSelectFilter({
  id,
  label,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  autoAlign = false,
}: MultiSelectFilterProps) {
  const selectedItems = options.filter((opt) => value.includes(opt.value));

  const triggerLabel =
    selectedItems.length === 0
      ? placeholder
      : selectedItems.length === 1
        ? selectedItems[0].label
        : `Selected ${label}`;

  return (
    <div className={styles.multiSelectFilter}>
      <FilterableMultiSelect
        key={`${id}-${value.length}`}
        id={id}
        autoAlign={autoAlign}
        titleText={label}
        placeholder={triggerLabel}
        items={options}
        initialSelectedItems={selectedItems}
        useTitleInItem
        itemToString={(item: MultiSelectItem | null) => item?.label ?? ''}
        onChange={({
          selectedItems,
        }: {
          selectedItems: readonly MultiSelectItem[];
        }) => onChange(selectedItems.map((item) => item.value))}
      />
    </div>
  );
}
